import { useMutation, useQueryClient } from '@tanstack/react-query';
import { demandaService } from '../../../../services/demanda';
import type { Demanda } from '../../../../models';
import { demandQueryKeys } from './useDemandQuery';

/**
 * Mutation para adicionar observadores com optimistic update
 */
export const useAddObserversMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { observadores: number[]; comentario?: string }) => {
      return demandaService.addObservadores(demandaId, data.observadores, data.comentario);
    },

    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      // Atualização otimista: adicionar novos observadores
      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        const newObservers = data.observadores.map(id => ({
          id,
          observador_fk: id,
          active: true,
        }));
        return {
          ...old,
          observadores: [...(old.observadores || []), ...newObservers],
        } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para remover observador com optimistic update
 */
export const useRemoveObserverMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (observadorId: number) => {
      return demandaService.removeObservador(demandaId, observadorId);
    },

    onMutate: async (observadorId) => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      // Atualização otimista: remover observador
      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return {
          ...old,
          observadores: (old.observadores || []).filter(
            (obs: any) => 
              (obs.observador_fk !== observadorId) && 
              (obs.id !== observadorId)
          ),
        } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para recusar ser observador com optimistic update
 */
export const useRefuseObserverMutation = (demandaId: number, currentUserId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return demandaService.recusarObservador(demandaId);
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      // Atualização otimista: remover usuário atual dos observadores
      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old || !currentUserId) return old;
        return {
          ...old,
          observadores: (old.observadores || []).filter(
            (obs: any) => 
              (obs.observador_fk !== currentUserId) && 
              (obs.id !== currentUserId)
          ),
        } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};


